import React, { useState, useRef, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import './PdfViewer.scss';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PdfViewerProps {
    currentPage: number;
    fileUrl: string;
}

const PdfViewer: React.FC<PdfViewerProps> = ({ currentPage, fileUrl }) => {
    const [numPages, setNumPages] = useState<number>(0);
    const [pageNumber, setPageNumber] = useState<number>(currentPage);
    const [scale, setScale] = useState<number>(1);
    const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setPageNumber(currentPage);
    }, [currentPage]);

    function onDocumentLoadSuccess({ numPages }: { numPages: number }): void {
        setNumPages(numPages);
        setPageNumber(currentPage);
    }

    const zoomIn = () => {
        setScale((prevScale) => Math.min(prevScale + 0.1, 2.0));
    };

    const zoomOut = () => {
        setScale((prevScale) => Math.max(prevScale - 0.1, 0.5));
    };

    const onPageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const page = parseInt(e.target.value);
        if (page >= 1 && page <= numPages) {
            setPageNumber(page);
        }
    };

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            containerRef.current?.requestFullscreen();
            setIsFullscreen(true);
        } else {
            document.exitFullscreen();
            setIsFullscreen(false);
        }
    };

    return (
        <div
            className={`pdf-viewer ${isFullscreen ? 'fullscreen' : ''}`}
            ref={containerRef}
        >
            <div className="pdf-viewer-header">
                <div className="pdf-controls">
                    <div className="zoom-control">
                        <span>Zoom</span>
                        <button className="zoom-button" onClick={zoomOut}>
                            -
                        </button>
                        <span>{Math.round(scale * 100)}%</span>
                        <button className="zoom-button" onClick={zoomIn}>
                            +
                        </button>
                    </div>
                    <div className="page-control">
                        <span>Page</span>
                        <input
                            type="number"
                            min={1}
                            max={numPages}
                            value={pageNumber}
                            onChange={onPageChange}
                            style={{ width: '100px' }}
                        />
                        <span>of {numPages}</span>
                    </div>
                    <div className="full-screen">
                        <button
                            className="expand-button"
                            onClick={toggleFullscreen}
                        >
                            <p>
                                {isFullscreen ? 'Exit Fullscreen' : 'Expand'}{' '}
                                <span className="Appkit4-icon icon-open-in-new-window-outline" />
                            </p>
                        </button>
                    </div>
                </div>
            </div>
            <div className="pdf-content">
                <Document
                    file={fileUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                    loading={<div>Loading PDF...</div>}
                >
                    <Page
                        pageNumber={pageNumber}
                        scale={scale}
                        renderAnnotationLayer={true}
                        renderTextLayer={true}
                    />
                </Document>
            </div>
        </div>
    );
};

export default PdfViewer;
